import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Filter from "../../Assets/Images/CommanImages/filter.png";
import columnaddition from "../../Assets/Images/CommanImages/rectangleplus.png";
import updown from "../../Assets/Images/CommanImages/updown.png";
import searchicon from "../../Assets/Images/CommanImages/searchyellow.png";
import leftArrow from "../../Assets/Images/CommanImages/Larrowbar.png";
import rightArrow from "../../Assets/Images/CommanImages/Rarrowbar.png";
import FileExport from "../../Assets/Images/CommanImages/fileexport.png";
import InterogativeImg from "./../../Assets/Images/CommanImages/Warning.png";
import SelectedValuesDropdown from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BatchTableDetails = ({
  headers,
  data,
  searchPlaceholder,
  dataClient,
  setPagePath,
  selectedRows,
  setSelectedRows,
  bulk = false,
}) => {
  const navigate = useNavigate();
  const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [averageRunTime, setAverageRunTime] = useState("");
  const isAllSelected = selectedRows.length === data.length;
  const columnDropdownRef = useRef(null);

  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [isFilterDropdownSelected, setIsFilterDropdownSelected] = useState(false);

  useEffect(() => {
    if (headers.length > 0) {
      const columnsToShow = headers
        .filter(h =>
          h !== "Batch ID" &&
          h !== "Session ID" &&
          h !== "Class ID" &&
          h !== "Course ID" &&
          h !== "Coaching Mode ID"
        )
        .slice(0, 7);
      setVisibleColumns(columnsToShow);
    }
  }, [headers]);

  const handleColumnSelection = (column) => {
    if (column === "Batch Name") return;
    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== column));
    } else {
      setVisibleColumns([...visibleColumns, column]);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleApplyFilter = () => {
    if (Object.keys(filters).length > 0) {
      setAppliedFilters({ ...filters });
      setCurrentPage(1);
      setIsFiltersApplied(true);
      setIsFilterVisible(false);
    }
  };

  const handleResetFilter = () => {
    setFilters({});
    setAppliedFilters({});
    setAverageRunTime("");
    setIsFiltersApplied(false);
  };

  const handleFilterSelection = (selectedOption, header) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [header]: selectedOption,
    }));
    setIsFilterDropdownSelected(true);
  };

  const filteredData = data.filter((row) => {
    const matchesSearchQuery = row.some(
      (cell) =>
        cell && String(cell).toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesAverageRunTime =
      averageRunTime === "" || row[2] === averageRunTime;

    const matchesFilters = Object.entries(appliedFilters).every(
      ([key, value]) => {
        const colIndex = headers.indexOf(key);
        return value ? row[colIndex] === value : true;
      }
    );

    return matchesSearchQuery && matchesAverageRunTime && matchesFilters;
  });

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setIsColumnDivVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const sortedData = React.useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig.key !== null) {
      const colIndex = headers.indexOf(sortConfig.key);
      sortableData.sort((a, b) => {
        if (a[colIndex] < b[colIndex]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[colIndex] > b[colIndex]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig, headers]);

  const handleExport = (type) => {
    const exportData = sortedData.map((row) =>
      visibleColumns.map((column) => row[headers.indexOf(column)])
    );

    const fileName = prompt("Enter the file name:");
    if (!fileName) return;

    if (type === "excel" || type === "csv") {
      const worksheet = XLSX.utils.aoa_to_sheet([visibleColumns, ...exportData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const fileExtension = type === "csv" ? "csv" : "xlsx";
      const mimeType =
        type === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const file = XLSX.write(workbook, { bookType: fileExtension, type: "array" });
      const blob = new Blob([file], { type: mimeType });
      FileSaver.saveAs(blob, `${fileName}.${fileExtension}`);
    } else if (type === "pdf") {
      const doc = new jsPDF();
      doc.autoTable({ head: [visibleColumns], body: exportData });
      doc.save(`${fileName}.pdf`);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            style={{ borderRadius: "5px" }}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(
            <button
              key={i}
              style={{ borderRadius: "5px" }}
              onClick={() => handlePageChange(i)}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else if (currentPage > 3 && currentPage < totalPages - 2) {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots2">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
      }
    }
    return pages;
  };

  const handleSelectAll = (e) => {
    if (!bulk) return;
    if (e.target.checked) {
      setSelectedRows(data.map((row) => row[0]));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (rowIndex) => {
    const actualIndex = (currentPage - 1) * pageSize + rowIndex;
    const questionId = data[actualIndex][0];

    if (bulk) {
      if (selectedRows.includes(questionId)) {
        setSelectedRows(selectedRows.filter((id) => id !== questionId));
      } else {
        setSelectedRows([...selectedRows, questionId]);
      }
    } else {
      setSelectedRows([questionId]);
    }
  };

  // Column max-widths by visible column index
  const getColumnMaxWidth = (index) => {
    switch (index) {
      case 0: return "160px"; // Batch Name
      case 1: return "180px"; // Description
      case 2: return "100px"; // Session
      case 3: return "110px"; // Class Name
      case 4: return "130px"; // Course Name
      case 5: return "160px"; // Batch Code
      default: return "130px";
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "1.5%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "82vw",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #6A6A6A",
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              placeholder={searchPlaceholder}
              name="search"
              className="search-input-ques-dash"
              onChange={handleSearch}
            />
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#2F2E40",
                margin: "0 4px",
              }}
            ></div>
            <img src={searchicon} alt="search" className="search-icon-ques-dash" />
          </div>
          <div style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}>
            Displaying{" "}
            {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)} -{" "}
            {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
            {sortedData.length} records |
            {["10", "25", "50", "100", "500"].map((size) => (
              <span
                key={size}
                onClick={() => handlePageSizeChange(Number(size))}
                style={{
                  cursor: "pointer",
                  margin: "0 5px",
                  textDecoration: size === pageSize.toString() ? "none" : "underline",
                  color: size === pageSize.toString() ? "black" : "#337AB7",
                }}
              >
                {size}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          backgroundColor: "#D6D6D6",
          marginTop: "9px",
          borderTop: "1px solid grey",
          borderBottom: "1px solid grey",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFBD20",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            paddingLeft: "8px",
            width: "90px",
          }}
          onClick={() => setIsFilterVisible(!isFilterVisible)}
        >
          <img src={Filter} style={{ height: "24px", width: "24px" }} alt="filter" />
          <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
        </div>
        <div
          className="col-tc"
          style={{
            marginLeft: "auto",
            textAlign: "end",
            marginRight: "8px",
            display: "flex",
            alignItems: "center",
            position: "relative",
            cursor: "pointer",
            height: "40px",
          }}
          onClick={() => setIsColumnDivVisible(!isColumnDivVisible)}
        >
          <img src={columnaddition} style={{ height: "34px", width: "32px" }} alt="column addition" />
          <span style={{ marginLeft: "4px", color: "#2F2E40", marginRight: "14px", fontSize: "14px" }}>
            Column Selection
          </span>
          {isColumnDivVisible && (
            <div ref={columnDropdownRef}>
              <div className="arrow-up-tc"></div>
              <div className="popupColumn-tc">
                {headers
                  .filter((header) =>
                    header &&
                    header !== "Batch ID" &&
                    header !== "Session ID" &&
                    header !== "Class ID" &&
                    header !== "Course ID" &&
                    header !== "Coaching Mode ID"
                  )
                  .map((column, index) => (
                    <div
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColumnSelection(column);
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`column-${index}`}
                        name="columnSelection"
                        value={column}
                        className="square-checkbox-tc"
                        checked={visibleColumns.includes(column)}
                        disabled={column === "Batch Name"}
                        readOnly
                      />
                      <label htmlFor={`column-${index}`}>{column}</label>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {data.length > 0 ? (
        <>
          <div
            className="table-container-scrollable"
            style={{
              width: "83vw",
              overflowX: "auto",
              overflowY: "visible",
              WebkitOverflowScrolling: "touch",
              boxSizing: "border-box",
            }}
          >
            <table className="fixed-header-table" style={{ width: "100%", tableLayout: "fixed" }}>
              <colgroup>
                {visibleColumns.map((_, index) => (
                  <col key={index} style={{ width: getColumnMaxWidth(index) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {visibleColumns.map((heading, index) => (
                    <td
                      key={index}
                      className="Heading-tc"
                      style={{
                        cursor: "pointer",
                        overflow: "hidden",
                        maxWidth: getColumnMaxWidth(index),
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                          color: "rgb(47, 46, 64)",
                          fontSize: "14px",
                          marginLeft: index === 0 ? "25px" : "0",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {heading}
                        <img
                          onClick={() => handleSort(heading)}
                          src={updown}
                          style={{ height: "20px", width: "20px", marginLeft: "5px", flexShrink: 0 }}
                          alt="sort"
                        />
                      </span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 1 ? "even-row-tc" : ""}>
                    {visibleColumns.map((column, cellIndex) => {
                      const dataIndex = headers.indexOf(column);
                      const cellValue = row[dataIndex];
                      const displayValue = cellValue !== null && cellValue !== undefined ? String(cellValue) : "---";

                      return (
                        <td
                          key={cellIndex}
                          style={{
                            textAlign: "left",
                            color: "#6A6A6A",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: getColumnMaxWidth(cellIndex),
                          }}
                        >
                          {cellIndex === 0 ? (
                            <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                              <input
                                type="checkbox"
                                className="delete-question-checkbox-dqc"
                                checked={selectedRows.includes(row[0])}
                                onChange={() => handleRowSelect(rowIndex)}
                                style={{ marginRight: "8px", flexShrink: 0 }}
                              />
                              <Link
                                style={{
                                  color: "#337ab7",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                  minWidth: 0,
                                }}
                                to={`/create_batch_edit?batchId=${row[0]}`}
                                state={{
                                  batch_id: row[0],
                                  batch_name: row[1],
                                  batch_description: row[2],
                                  session: row[3],
                                  session_id: row[4],
                                  class_id: row[5],
                                  class_name: row[6],
                                  course_id: row[7],
                                  course_name: row[8],
                                  coaching_mode: row[9],
                                  status: row[10],
                                  batch_code: row[11],
                                  coaching_mode_id: row[12],
                                  exam_date: row[13],
                                  created_by: row[14],
                                  created_on: row[15],
                                }}
                                title={displayValue}
                              >
                                {displayValue}
                              </Link>
                            </div>
                          ) : (
                            <span
                              className="truncated-text-2"
                              title={displayValue}
                              style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {displayValue}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-tc">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="extreme-left-tc-pagination"
            >
              <img src={leftArrow} alt="" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="lefttc-pagination"
            >
              {"<"}
            </button>
            {renderPagination()}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="righttc-pagination"
            >
              {">"}
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="extreme-right-tc-pagination"
            >
              <img src={rightArrow} alt="" />
            </button>
          </div>
        </>
      ) : (
        "No data available"
      )}

      {isFilterVisible && (
        <div className="filter-sidebar-tc">
          <div className="filter-content-tc" style={{ position: "relative" }}>
            <button
              className="repman-cross-btn-HOD"
              onClick={() => setIsFilterVisible(false)}
            >
              <p>X</p>
            </button>
            <h3 className="AdvanceF-heading-tc">Filter</h3>
            <div className="filter-apply-reset-ques-rev-dash">
              <button className="create-test-section-btn" onClick={handleApplyFilter}>
                APPLY
              </button>
              {isFiltersApplied && (
                <button className="create-test-section-btn" onClick={handleResetFilter}>
                  RESET
                </button>
              )}
            </div>
            <div className="filter-options-container">
              {headers
                .filter((header) =>
                  header !== "Batch ID" &&
                  header !== "Session ID" &&
                  header !== "Class ID" &&
                  header !== "Course ID" &&
                  header !== "Coaching Mode ID"
                )
                .map((header) => {
                  const originalIndex = headers.indexOf(header);
                  let uniqueOptions = [
                    ...new Set(
                      data.map((row) => row[originalIndex]).filter((option) => option)
                    ),
                  ];
                  if (originalIndex === 1 || originalIndex === 6) {
                    uniqueOptions.sort((a, b) => (a > b ? 1 : -1));
                  }
                  return (
                    <SelectedValuesDropdown
                      key={originalIndex}
                      label={header}
                      width="300px"
                      marginLeft="15px"
                      marginTop="18px"
                      setSelected={(selectedOption) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          [header]: selectedOption,
                        }));
                      }}
                      options={uniqueOptions.map((value) => ({ label: value, id: value }))}
                      selected={filters[header] || ""}
                      Height="auto"
                      OverFlow="auto"
                      minheight="200px"
                    />
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchTableDetails;









// import React, { useState, useEffect, useRef } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import { Link } from "react-router-dom";
// import Filter from "../../Assets/Images/CommanImages/filter.png";
// import columnaddition from "../../Assets/Images/CommanImages/rectangleplus.png";
// import updown from "../../Assets/Images/CommanImages/updown.png";
// import searchicon from "../../Assets/Images/CommanImages/searchyellow.png";
// import leftArrow from "../../Assets/Images/CommanImages/Larrowbar.png";
// import rightArrow from "../../Assets/Images/CommanImages/Rarrowbar.png";
// import FileExport from "../../Assets/Images/CommanImages/fileexport.png";
// import InterogativeImg from "./../../Assets/Images/CommanImages/Warning.png";
// import SelectedValuesDropdown from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
// import FileSaver from "file-saver";
// import * as XLSX from "xlsx";
// import jsPDF from "jspdf";
// import "jspdf-autotable";

// const BatchTableDetails = ({
//   headers,
//   data,
//   searchPlaceholder,
//   dataClient,
//   setPagePath,
//   renderQuestionText,
//   selectedRows,
//   setSelectedRows,
//   bulk = false,
// }) => {
//   const navigate = useNavigate();
//   const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
//   const [isFilterVisible, setIsFilterVisible] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [visibleColumns, setVisibleColumns] = useState([]);
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
//   const [isModalVisible, setIsModalVisible] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(10);
//   const [averageRunTime, setAverageRunTime] = useState("");
//   const isAllSelected = selectedRows.length === data.length;
//   const columnDropdownRef = useRef(null);

//   const [filters, setFilters] = useState({}); // Holds current selections
//   const [appliedFilters, setAppliedFilters] = useState({});
//   const [isFiltersApplied, setIsFiltersApplied] = useState(false);
//   const [isFilterDropdownSelected, setIsFilterDropdownSelected] = useState(false);

//    useEffect(() => {
//     if (headers.length > 0) {
//       const columnsToShow = headers
//         .filter(h => 
//           h !== "Batch ID" && 
//           h !== "Session ID" && 
//           h !== "Class ID" && 
//           h !== "Course ID" && 
//           h !== "Coaching Mode ID"
//         )
//         .slice(0, 6);
//       setVisibleColumns(columnsToShow);
//     }
//   }, [headers]);

//   const handleColumnSelection = (column) => {
//     if (column === "Batch Name") return; // lock it
//     if (visibleColumns.includes(column)) {
//       setVisibleColumns(visibleColumns.filter((col) => col !== column));
//     } else {
//       setVisibleColumns([...visibleColumns, column]);
//     }
//   };

//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//     setCurrentPage(1);
//   };

//   const handleApplyFilter = () => {
//     if (Object.keys(filters).length > 0) {
//       setAppliedFilters({ ...filters }); // Persist selected filters
//       setCurrentPage(1); // Reset to the first page
//       setIsFiltersApplied(true); // Show Reset button
//       setIsFilterVisible(false); // Hide the filter sidebar
//     }
//   };

//   const handleResetFilter = () => {
//     setFilters({}); // Clear current filter selections
//     setAppliedFilters({}); // Clear applied filters
//     setAverageRunTime(""); // Reset any other state if required
//     setIsFiltersApplied(false); // Deactivate the Reset button
//   };

//   const handleFilterSelection = (selectedOption, header) => {
//     setFilters((prevFilters) => ({
//       ...prevFilters,
//       [header]: selectedOption,
//     }));

//     setIsFilterDropdownSelected(true); // Mark dropdown as interacted
//   };

//   const filteredData = data.filter((row) => {
//     const matchesSearchQuery = row.some(
//       (cell) =>
//         cell && String(cell).toLowerCase().includes(searchQuery.toLowerCase())
//     );
//     const matchesAverageRunTime =
//       averageRunTime === "" || row[2] === averageRunTime;

//     // Apply appliedFilters
//     const matchesFilters = Object.entries(appliedFilters).every(
//       ([key, value]) => {
//         const colIndex = headers.indexOf(key);
//         return value ? row[colIndex] === value : true;
//       }
//     );

//     return matchesSearchQuery && matchesAverageRunTime && matchesFilters;
//   });

//   const handleSort = (column) => {
//     let direction = "asc";
//     if (sortConfig.key === column && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key: column, direction });
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         columnDropdownRef.current &&
//         !columnDropdownRef.current.contains(event.target)
//       ) {
//         setIsColumnDivVisible(false); // Close the dropdown if clicked outside
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const sortedData = React.useMemo(() => {
//     let sortableData = [...filteredData];
//     if (sortConfig.key !== null) {
//       const colIndex = headers.indexOf(sortConfig.key);
//       sortableData.sort((a, b) => {
//         if (a[colIndex] < b[colIndex]) {
//           return sortConfig.direction === "asc" ? -1 : 1;
//         }
//         if (a[colIndex] > b[colIndex]) {
//           return sortConfig.direction === "asc" ? 1 : -1;
//         }
//         return 0;
//       });
//     }
//     return sortableData;
//   }, [filteredData, sortConfig, headers]);

//   const handleExport = (type) => {
//     const exportData = sortedData.map((row) =>
//       visibleColumns.map((column) => row[headers.indexOf(column)])
//     );

//     const fileName = prompt("Enter the file name:");

//     if (!fileName) {
//       return;
//     }

//     if (type === "excel" || type === "csv") {
//       const worksheet = XLSX.utils.aoa_to_sheet([
//         visibleColumns,
//         ...exportData,
//       ]);
//       const workbook = XLSX.utils.book_new();
//       XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

//       const fileExtension = type === "csv" ? "csv" : "xlsx";
//       const mimeType =
//         type === "csv"
//           ? "text/csv"
//           : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

//       const file = XLSX.write(workbook, {
//         bookType: fileExtension,
//         type: "array",
//       });
//       const blob = new Blob([file], { type: mimeType });
//       FileSaver.saveAs(blob, `${fileName}.${fileExtension}`);
//     } else if (type === "pdf") {
//       const doc = new jsPDF();
//       doc.autoTable({
//         head: [visibleColumns],
//         body: exportData,
//       });

//       doc.save(`${fileName}.pdf`);
//     }
//   };

//   const handlePageSizeChange = (size) => {
//     setPageSize(size);
//     setCurrentPage(1);
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const totalPages = Math.ceil(sortedData.length / pageSize);
//   const paginatedData = React.useMemo(() => {
//     const startIndex = (currentPage - 1) * pageSize;
//     return sortedData.slice(startIndex, startIndex + pageSize);
//   }, [sortedData, currentPage, pageSize]);

//   const renderPagination = () => {
//     const pages = [];
//     if (totalPages <= 5) {
//       for (let i = 1; i <= totalPages; i++) {
//         pages.push(
//           <button
//             key={i}
//             onClick={() => handlePageChange(i)}
//             style={{ borderRadius: "5px" }}
//             className={i === currentPage ? "active" : ""}
//           >
//             {i}
//           </button>
//         );
//       }
//     } else {
//       if (currentPage <= 3) {
//         for (let i = 1; i <= 3; i++) {
//           pages.push(
//             <button
//               key={i}
//               style={{ borderRadius: "5px" }}
//               onClick={() => handlePageChange(i)}
//               className={i === currentPage ? "active" : ""}
//             >
//               {i}
//             </button>
//           );
//         }
//         pages.push(
//           <span style={{ borderRadius: "5px" }} key="dots1">
//             ...
//           </span>
//         );
//         pages.push(
//           <button
//             key={totalPages}
//             onClick={() => handlePageChange(totalPages)}
//             style={{ borderRadius: "5px" }}
//           >
//             {totalPages}
//           </button>
//         );
//       } else if (currentPage > 3 && currentPage < totalPages - 2) {
//         pages.push(
//           <button
//             key={1}
//             onClick={() => handlePageChange(1)}
//             style={{ borderRadius: "5px" }}
//           >
//             1
//           </button>
//         );
//         pages.push(
//           <span style={{ borderRadius: "5px" }} key="dots1">
//             ...
//           </span>
//         );
//         for (let i = currentPage - 1; i <= currentPage + 1; i++) {
//           pages.push(
//             <button
//               key={i}
//               onClick={() => handlePageChange(i)}
//               style={{ borderRadius: "5px" }}
//               className={i === currentPage ? "active" : ""}
//             >
//               {i}
//             </button>
//           );
//         }
//         pages.push(
//           <span style={{ borderRadius: "5px" }} key="dots2">
//             ...
//           </span>
//         );
//         pages.push(
//           <button
//             key={totalPages}
//             onClick={() => handlePageChange(totalPages)}
//             style={{ borderRadius: "5px" }}
//           >
//             {totalPages}
//           </button>
//         );
//       } else {
//         pages.push(
//           <button
//             key={1}
//             onClick={() => handlePageChange(1)}
//             style={{ borderRadius: "5px" }}
//           >
//             1
//           </button>
//         );
//         pages.push(
//           <span style={{ borderRadius: "5px" }} key="dots1">
//             ...
//           </span>
//         );
//         for (let i = totalPages - 2; i <= totalPages; i++) {
//           pages.push(
//             <button
//               key={i}
//               onClick={() => handlePageChange(i)}
//               style={{ borderRadius: "5px" }}
//               className={i === currentPage ? "active" : ""}
//             >
//               {i}
//             </button>
//           );
//         }
//       }
//     }
//     return pages;
//   };

//   const handleSelectAll = (e) => {
//     if (!bulk) {
//       // If not in bulk mode, don't allow selecting all
//       return;
//     }
//     if (e.target.checked) {
//       setSelectedRows(data.map((row) => row[0]));
//     } else {
//       setSelectedRows([]);
//     }
//   };

//   // Modify handleRowSelect to handle single/multi select
//   const handleRowSelect = (rowIndex) => {
//     const actualIndex = (currentPage - 1) * pageSize + rowIndex;
//     const questionId = data[actualIndex][0];

//     if (bulk) {
//       // Multi-select mode
//       if (selectedRows.includes(questionId)) {
//         setSelectedRows(selectedRows.filter((id) => id !== questionId));
//       } else {
//         setSelectedRows([...selectedRows, questionId]);
//       }
//     } else {
//       // Single-select mode
//       setSelectedRows([questionId]);
//     }
//   };

//   const [columnWidth, setColumnWidth] = useState(300);
//   const columnRef = useRef(null);
//   const isResizing = useRef(false);

//   const handleMouseDown = (event) => {
//     isResizing.current = true;
//     document.addEventListener("mousemove", handleMouseMove);
//     document.addEventListener("mouseup", handleMouseUp);
//   };

//   const handleMouseMove = (event) => {
//     if (isResizing.current) {
//       const newWidth = Math.max(
//         100,
//         event.clientX - columnRef.current.offsetLeft
//       ); // Min width: 100px
//       setColumnWidth(newWidth);
//     }
//   };

//   const handleMouseUp = () => {
//     isResizing.current = false;
//     document.removeEventListener("mousemove", handleMouseMove);
//     document.removeEventListener("mouseup", handleMouseUp);
//   };

//   return (
//     <div>
//       <div
//         style={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           marginTop: "1.5%",
//         }}
//       >
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//             width: "82vw",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               border: "1px solid #6A6A6A",
//               overflow: "hidden",
//             }}
//           >
//             <input
//               type="text"
//               placeholder={searchPlaceholder}
//               name="search"
//               className="search-input-ques-dash"
//               onChange={handleSearch}
//             />
//             <div
//               style={{
//                 width: "1px",
//                 height: "24px",
//                 backgroundColor: "#2F2E40",
//                 margin: "0 4px",
//               }}
//             ></div>
//             <img
//               src={searchicon}
//               alt="search"
//               className="search-icon-ques-dash"
//             />
//           </div>
//           <div
//             style={{ textAlign: "end", marginTop: "20px", color: "#424242" }}
//           >
//             Displaying{" "}
//             {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)} -{" "}
//             {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
//             {sortedData.length} records |
//             {["10", "25", "50", "100", "500"].map((size) => (
//               <span
//                 key={size}
//                 onClick={() => handlePageSizeChange(Number(size))}
//                 style={{
//                   cursor: "pointer",
//                   margin: "0 5px",
//                   textDecoration:
//                     size === pageSize.toString() ? "none" : "underline",
//                   color: size === pageSize.toString() ? "black" : "#337AB7",
//                 }}
//               >
//                 {size}
//               </span>
//             ))}
//           </div>
//         </div>
//       </div>
//       <div
//         style={{
//           display: "flex",
//           backgroundColor: "#D6D6D6",
//           marginTop: "9px",
//           borderTop: "1px solid grey",
//           borderBottom: "1px solid grey",
//         }}
//       >
//         <div
//           style={{
//             backgroundColor: "#FFBD20",
//             display: "flex",
//             alignItems: "center",
//             cursor: "pointer",
//             paddingLeft: "8px",
//             width: "90px",
//           }}
//           onClick={() => setIsFilterVisible(!isFilterVisible)}
//         >
//           <img
//             src={Filter}
//             style={{ height: "24px", width: "24px" }}
//             alt="filter"
//           />
//           <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Filter</span>
//         </div>
//         <div
//           className="col-tc"
//           style={{
//             marginLeft: "auto",
//             textAlign: "end",
//             marginRight: "8px",
//             display: "flex",
//             alignItems: "center",
//             position: "relative",
//             cursor: "pointer",
//             height: "40px",
//           }}
//           onClick={() => setIsColumnDivVisible(!isColumnDivVisible)}
//         >
//           <img
//             src={columnaddition}
//             style={{ height: "34px", width: "32px" }}
//             alt="column addition"
//           />
//           <span
//             style={{
//               marginLeft: "4px",
//               color: "#2F2E40",
//               marginRight: "14px",
//               fontSize: "14px",
//             }}
//           >
//             Column Selection
//           </span>
//           {isColumnDivVisible && (
//             <div ref={columnDropdownRef}>
//               <div className="arrow-up-tc"></div>
//               <div className="popupColumn-tc">
//                 {headers
//                   // Remove only columns you want to hide from selection, e.g., "Status"
//                   // .filter((header) => header)
//                  .filter((header) => 
//                     header && 
//                     header !== "Batch ID" && 
//                     header !== "Session ID" && 
//                     header !== "Class ID" && 
//                     header !== "Course ID" && 
//                     header !== "Coaching Mode ID"
//                   )
//                   .map((column, index) => (
//                     <div
//                       key={index}
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         handleColumnSelection(column);
//                       }}
//                     >
//                       <input
//                         type="checkbox"
//                         id={`column-${index}`}
//                         name="columnSelection"
//                         value={column}
//                         className="square-checkbox-tc"
//                         checked={visibleColumns.includes(column)}
//                         disabled={column === "Batch Name"}
//                         readOnly
//                       />
//                       <label htmlFor={`column-${index}`}>{column}</label>
//                     </div>
//                   ))}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//       {data.length > 0 ? (
//         <>
//           <div className="table-container-scrollable"
//            style={{
//               width: "83vw",
//               overflowX: "auto",
//               overflowY: "visible",
//               WebkitOverflowScrolling: "touch",
//               boxSizing: "border-box"
//             }}
//             >
//             <table className="fixed-header-table" style={{ width: "100%" }}>
//               <thead>
//                 <tr>
//                   {visibleColumns.map((heading, index) => (
//                     <td
//                       key={index}
//                       ref={index === 1 ? columnRef : null}
//                       className={`Heading-tc resizable-header ${
//                         index === 1 ? "resizable-header" : ""
//                       }`}
//                       style={{
//                         textAlign: "right",
//                         cursor: "pointer",
//                         width: index === 1 ? `${columnWidth}px` : "auto",
//                         position: "relative",
//                       }}
//                     >
//                       <span
//                         style={{
//                           display: "flex",
//                           alignItems: "left",
//                           justifyContent: "left",
//                           color: "rgb(47, 46, 64)", 
//                           fontSize: "14px",
//                           marginLeft: index === 0 ? "25px" : "0",
//                         }}
//                       >
//                         {heading}
//                         {index === 1 && (
//                           <div
//                             className="resize-handle"
//                             onMouseDown={handleMouseDown}
//                           ></div>
//                         )}
//                         <img
//                           onClick={() => handleSort(heading)}
//                           src={updown}
//                           style={{
//                             height: "20px",
//                             width: "20px",
//                             marginLeft: "5px",
//                           }}
//                           alt="sort"
//                         />
//                       </span>
//                     </td>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {paginatedData.map((row, rowIndex) => (
//                   <tr
//                     key={rowIndex}
//                     className={rowIndex % 2 === 1 ? "even-row-tc" : ""}
//                   >
//                     {visibleColumns.map((column, cellIndex) => (
//                       <td
//                         key={cellIndex}
//                         style={{
//                           width: cellIndex === 1 ? `${columnWidth}px` : "auto",
//                           textAlign: "left",
//                           color: "#6A6A6A",
//                           verticalAlign: "middle",
//                           whiteSpace: "nowrap",
//                           overflow: "hidden",
//                           textOverflow: "ellipsis",
//                           maxWidth: `${columnWidth}px`,
//                         }}
//                       >
//                         {cellIndex === 0 ? (
//                           <div
//                             style={{ display: "flex", alignItems: "center" }}
//                           >
//                             <input
//                               type="checkbox"
//                               className="delete-question-checkbox-dqc"
//                               checked={selectedRows.includes(row[0])} // Check against row ID
//                               onChange={() => handleRowSelect(rowIndex)}
//                               style={{ marginRight: "8px" }}
//                             />
//                             {/* <Link
//                               style={{ color: "#337ab7" }}
//                               to={`/create_batch_edit?batchId=${row[0]}`}
//                             > */}
//                            <Link
//                                 style={{ color: "#337ab7" }}
//                                 to={`/create_batch_edit?batchId=${row[0]}`}
//                                 state={{
//                                   batch_id: row[0],
//                                   batch_name: row[1],
//                                   batch_description: row[2],
//                                   session: row[3],
//                                   session_id: row[4],
//                                   class_id: row[5],
//                                   class_name: row[6],
//                                   course_id: row[7],
//                                   course_name: row[8],
//                                   batch_code: row[9],
//                                   coaching_mode_id: row[10],
//                                   coaching_mode: row[11],
//                                   exam_date: row[12],
//                                   status: row[13],
//                                   created_by: row[14],
//                                   created_on: row[15]
//                                 }}
//                               >
//                                 <span
//                                 className="truncated-text"
//                                 title={row[headers.indexOf(column)]}
//                               >
//                                 {headers.indexOf(column) === 2
//                                   ? renderQuestionText(
//                                       row[headers.indexOf(column)]
//                                     ) // Render LaTeX here
//                                   : row[headers.indexOf(column)]}
//                               </span>
//                             </Link>
//                           </div>
//                         ) : (
//                           <span className="truncated-text-2">
//                             {headers.indexOf(column) === 2
//                               ? renderQuestionText(row[headers.indexOf(column)]) // Render LaTeX for LaTeX column
//                               : row[headers.indexOf(column)]}
//                           </span>
//                         )}
//                       </td>
//                     ))}
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//           <div className="pagination-tc">
//             <button
//               onClick={() => handlePageChange(1)}
//               disabled={currentPage === 1}
//               className="extreme-left-tc-pagination"
//             >
//               <img src={leftArrow} alt="" />
//             </button>
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="lefttc-pagination"
//             >
//               {"<"}
//             </button>
//             {renderPagination()}
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="righttc-pagination"
//             >
//               {">"}
//             </button>
//             <button
//               onClick={() => handlePageChange(totalPages)}
//               disabled={currentPage === totalPages}
//               className="extreme-right-tc-pagination"
//             >
//               <img src={rightArrow} alt="" />
//             </button>
//           </div>
//         </>
//       ) : (
//         "No data available"
//       )}
//       {isFilterVisible && (
//         <div className="filter-sidebar-tc">
//           <div className="filter-content-tc" style={{ position: "relative" }}>
//             <button
//               className="repman-cross-btn-HOD"
//               onClick={() => {
//                 setIsFilterVisible(false);
//               }}
//             >
//               <p>X</p>
//             </button>
//             <h3 className="AdvanceF-heading-tc"> Filter</h3>
//             <div className="filter-apply-reset-ques-rev-dash">
//               {/* Show the Reset button only after Apply is clicked */}

//               {/* Always show the Apply button */}
//               <button
//                 // className="apply-btn-question-review-dash"
//                 className="create-test-section-btn"
//                 onClick={handleApplyFilter}
//               >
//                 APPLY
//               </button>
//               {isFiltersApplied && (
//                 <button
//                   className="create-test-section-btn"
//                   onClick={handleResetFilter}
//                 >
//                   RESET
//                 </button>
//               )}
//             </div>
//             <div className="filter-options-container">
//             {headers
//               .filter((header) => 
//                 header !== "Batch ID" && 
//                 header !== "Session ID" && 
//                 header !== "Class ID" && 
//                 header !== "Course ID" && 
//                 header !== "Coaching Mode ID"
//               )
//               .map((header) => {
//                 const originalIndex = headers.indexOf(header);

//                 let uniqueOptions = [
//                   ...new Set(
//                     data
//                       .map((row) => row[originalIndex])
//                       .filter((option) => option)
//                   ),
//                 ];

//                 if (originalIndex === 1 || originalIndex === 6) {
//                   uniqueOptions.sort((a, b) => (a > b ? 1 : -1));
//                 }

//                 return (
//                   <SelectedValuesDropdown
//                     key={originalIndex}
//                     label={header}
//                     width="300px"
//                     marginLeft="15px"
//                     marginTop="18px"
//                     setSelected={(selectedOption) => {
//                       setFilters((prevFilters) => ({
//                         ...prevFilters,
//                         [header]: selectedOption,
//                       }));
//                     }}
//                     options={uniqueOptions.map((value) => ({
//                       label: value,
//                       id: value,
//                     }))}
//                     selected={filters[header] || ""}
//                     Height="auto"
//                     OverFlow="auto"
//                     minheight="200px"
//                   />
//                 );
//               })}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };
// export default BatchTableDetails;




